// Flex.tsx
import { css } from "@pigment-css/react";
import { clsx } from "clsx";

const baseStyle = css({
  display: "flex",
});

// Static spacing styles for gap using managementView spacing tokens
const gapStyles = {
  2: css(({ theme }) => ({ gap: theme.managementView.spacing["2"] })),
  4: css(({ theme }) => ({ gap: theme.managementView.spacing["4"] })),
  6: css(({ theme }) => ({ gap: theme.managementView.spacing["6"] })),
  8: css(({ theme }) => ({ gap: theme.managementView.spacing["8"] })),
  10: css(({ theme }) => ({ gap: theme.managementView.spacing["10"] })),
  12: css(({ theme }) => ({ gap: theme.managementView.spacing["12"] })),
  14: css(({ theme }) => ({ gap: theme.managementView.spacing["14"] })),
  16: css(({ theme }) => ({ gap: theme.managementView.spacing["16"] })),
  18: css(({ theme }) => ({ gap: theme.managementView.spacing["18"] })),
  24: css(({ theme }) => ({ gap: theme.managementView.spacing["24"] })),
  26: css(({ theme }) => ({ gap: theme.managementView.spacing["26"] })),
  32: css(({ theme }) => ({ gap: theme.managementView.spacing["32"] })),
  48: css(({ theme }) => ({ gap: theme.managementView.spacing["48"] })),
  64: css(({ theme }) => ({ gap: theme.managementView.spacing["64"] })),
};

// Static spacing styles for columnGap using managementView spacing tokens
const columnGapStyles = {
  2: css(({ theme }) => ({ columnGap: theme.managementView.spacing["2"] })),
  4: css(({ theme }) => ({ columnGap: theme.managementView.spacing["4"] })),
  6: css(({ theme }) => ({ columnGap: theme.managementView.spacing["6"] })),
  8: css(({ theme }) => ({ columnGap: theme.managementView.spacing["8"] })),
  10: css(({ theme }) => ({ columnGap: theme.managementView.spacing["10"] })),
  12: css(({ theme }) => ({ columnGap: theme.managementView.spacing["12"] })),
  14: css(({ theme }) => ({ columnGap: theme.managementView.spacing["14"] })),
  16: css(({ theme }) => ({ columnGap: theme.managementView.spacing["16"] })),
  18: css(({ theme }) => ({ columnGap: theme.managementView.spacing["18"] })),
  24: css(({ theme }) => ({ columnGap: theme.managementView.spacing["24"] })),
  26: css(({ theme }) => ({ columnGap: theme.managementView.spacing["26"] })),
  32: css(({ theme }) => ({ columnGap: theme.managementView.spacing["32"] })),
  48: css(({ theme }) => ({ columnGap: theme.managementView.spacing["48"] })),
  64: css(({ theme }) => ({ columnGap: theme.managementView.spacing["64"] })),
};

const directionStyles = {
  row: css({ flexDirection: "row" }),
  column: css({ flexDirection: "column" }),
};

const alignStyles = {
  start: css({ alignItems: "flex-start" }),
  center: css({ alignItems: "center" }),
  end: css({ alignItems: "flex-end" }),
  stretch: css({ alignItems: "stretch" }),
  baseline: css({ alignItems: "baseline" }),
};

const justifyStyles = {
  start: css({ justifyContent: "flex-start" }),
  center: css({ justifyContent: "center" }),
  end: css({ justifyContent: "flex-end" }),
  between: css({ justifyContent: "space-between" }),
};

const wrapStyles = {
  nowrap: css({ flexWrap: "nowrap" }),
  wrap: css({ flexWrap: "wrap" }),
};

export const Flex = ({
  direction = "row",
  gap = 8,
  align,
  justify = "start",
  className,
  children,
  wrap = false,
  columnGap,
  ...props
}: {
  direction?: keyof typeof directionStyles;
  gap?: keyof typeof gapStyles;
  columnGap?: keyof typeof gapStyles;
  align?: keyof typeof alignStyles;
  justify?: keyof typeof justifyStyles;
  wrap?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const effectiveAlign = align ?? (direction === "column" ? "stretch" : "center");

  return (
    <div
      className={clsx(
        baseStyle,
        directionStyles[direction],
        gapStyles[gap],
        columnGap && columnGapStyles[columnGap],
        alignStyles[effectiveAlign],
        justifyStyles[justify],
        wrap ? wrapStyles.wrap : wrapStyles.nowrap,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};
